import React, { FC, Fragment, useEffect } from 'react';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { CONSTANTS } from 'config/constants';
import Box from '@mui/material/Box';
import { Sample } from 'models/order';
import { StyledToggleButton } from 'components/shared/styled-toggle-button/styled-toggle-button.component';

type SampleProps = {
    handleSampleSelection: (sample: Sample) => void;
    sample: Sample;
    dataTest?: string;
};
const SampleMethod: FC<SampleProps> = ({ handleSampleSelection, sample, dataTest }: SampleProps) => {
    const { t: translate } = useTranslation(['orders']);
    const sampleMethods = CONSTANTS.SAMPLE_METHODS;

    const [sampleData, setSample] = React.useState<Sample>({
        type: '',
        content: [],
        otherContent: '',
    });

    useEffect(() => {
        setSample({
            ...sample,
            content: sample.content && sample.content.length > 0 ? sample.content : defaultSampleContent,
        });
    }, [sample]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const sampleObj = Object.assign({}, sampleData);
        sampleObj.type = (event.target as HTMLInputElement).value;
        const content = JSON.parse(JSON.stringify(sampleObj.content));
        content?.forEach((content: { name: string; value: boolean }) => {
            content.value = false;
        });
        sampleObj.content = content;
        sampleObj.otherContent = '';
        handleSampleSelection(sampleObj);
    };

    const handleContent = (content: { name: string; value: boolean }) => {
        const contentObj = Object.assign({}, content);
        const sampleObj = Object.assign({}, sampleData);
        const index = (sampleObj.content as Array<{ name: string; value: boolean }>).findIndex(
            (cont) => cont.name === contentObj.name,
        );
        if (index !== -1) {
            contentObj.value = !contentObj.value;
            const tempArr = Object.assign([], sampleObj.content);
            tempArr.splice(index, 1, contentObj);
            sampleObj.content = tempArr;
        }
        handleSampleSelection(sampleObj);
    };

    const handleOtherContent = (event: React.ChangeEvent) => {
        const sampleObj = Object.assign({}, sampleData);
        sampleObj.otherContent = (event.target as HTMLInputElement).value;
        handleSampleSelection(sampleObj);
    };

    return (
        <Fragment>
            <FormControl fullWidth component="fieldset">
                <RadioGroup row name="sampleMethods" value={sampleData.type} onChange={handleChange}>
                    {sampleMethods.map((elem) => (
                        <FormControlLabel
                            key={elem}
                            value={elem}
                            control={<Radio color="secondary" data-test={`${dataTest}-${elem}`} />}
                            label={translate(elem)}
                        />
                    ))}
                </RadioGroup>
            </FormControl>

            {sample.type === 'PHYSICAL' ? (
                <Fragment>
                    <Box mt={1}>
                        <ToggleButtonGroup value={sampleData.content} sx={{ display: 'flex', flexWrap: 'wrap' }}>
                            {(sampleData.content as Array<{ name: string; value: boolean }>).map(
                                (content: { name: string; value: boolean }) => {
                                    return (
                                        <StyledToggleButton
                                            key={content.name}
                                            value={content.name}
                                            sx={(theme) => ({
                                                ...(content.value && {
                                                    background: theme.palette.primary.main,
                                                    color: '#fff !important',
                                                    '&:hover': { background: theme.palette.primary.main },
                                                }),
                                            })}
                                            onClick={() => handleContent(content)}
                                            data-test={`${dataTest}-content-${content.name}`}
                                        >
                                            {translate(content.name)}
                                        </StyledToggleButton>
                                    );
                                },
                            )}
                        </ToggleButtonGroup>
                    </Box>
                    <Box mt={2} display="flex" width={400}>
                        <TextField
                            id="physical-sample"
                            fullWidth
                            value={sampleData.otherContent}
                            label={translate('OTHER_CONTENT')}
                            onChange={handleOtherContent}
                            data-test={`${dataTest}-other-content-txt`}
                        />
                    </Box>
                </Fragment>
            ) : undefined}
        </Fragment>
    );
};

const defaultSampleContent = [
    {
        name: 'SAMPLE',
        value: false,
    },
    {
        name: 'STUDY_MODEL',
        value: false,
    },
    {
        name: 'INDEX',
        value: false,
    },
    {
        name: 'PLASTER_MODEL',
        value: false,
    },
    {
        name: 'IMPLANT_COMPONENTS',
        value: false,
    },
    {
        name: 'DOWEL_PINS',
        value: false,
    },
    {
        name: 'COLOR_SAMPLE',
        value: false,
    },
];

export default SampleMethod;
